/**
 * New Pricing Plans Page - Simplified Layout
 */
import type { Route } from "./+types/pricing";

import { CheckIcon, XIcon } from "lucide-react";
import { Link, useLoaderData } from "react-router";

import { Button } from "~/core/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/core/components/ui/card";
import makeServerClient from "~/core/lib/supa-client.server";
import { getUserSubscription, isPaidUser } from "~/features/ai/queries";

// Loader - Check user authentication and subscription status
export async function loader({ request }: Route.LoaderArgs) {
  const [supabase] = makeServerClient(request);

  const {
    data: { user },
  } = await supabase.auth.getUser();

  // Get user payment status and subscription info if authenticated
  let userIsPaid = false;
  let subscriptionInfo = null;

  if (user) {
    try {
      console.log("🔍 Loading user data for:", user.email);
      userIsPaid = await isPaidUser(supabase, user.id);
      console.log("🔍 isPaidUser result:", userIsPaid);

      subscriptionInfo = await getUserSubscription(supabase, user.id);
      console.log("🔍 subscriptionInfo result:", subscriptionInfo);
    } catch (error) {
      console.warn("Failed to load user payment status:", error);
    }
  }

  return {
    user,
    isAuthenticated: !!user,
    isPaidUser: userIsPaid,
    subscriptionInfo,
  };
}

export default function PricingPage() {
  const {
    user,
    isAuthenticated,
    isPaidUser: userIsPaid,
    subscriptionInfo,
  } = useLoaderData<typeof loader>();

  // 디버깅 정보
  console.log("🔍 Pricing Page Debug:", {
    user: user?.email,
    isAuthenticated,
    userIsPaid,
    subscriptionInfo,
  });

  // Free Plan Data
  const freePlan = {
    name: "Free",
    price: "무료",
    description: "AI 내동생을 경험해보세요",
    features: [
      { text: "하루 3회 AI 피드백", included: true },
      { text: "기본 개념 설명", included: true },
      { text: "채팅 기능", included: true },
      { text: "학습 이력 보기", included: true },
      { text: "무제한 사용", included: false },
      { text: "고급 분석", included: false },
      { text: "우선 지원", included: false },
    ],
  };

  // Pro Packages
  const premiumPackages = [
    {
      name: "1일권",
      price: "₩2,900",
      period: "/24시간",
      description: "부담없이 체험하기",
      discount: "체험 특가",
      originalPrice: null,
      link: `/payments/checkout?plan=1day&price=2900`,
      buttonText: "1일권 시작하기",
      isDaily: true, // 일일권 표시용
    },
    {
      name: "1개월",
      price: "₩12,900",
      period: "/월",
      description: "일시결제",
      discount: null,
      originalPrice: null,
      link: `/payments/checkout?plan=1month&price=12900`,
      buttonText: "1개월 시작하기",
    },
    {
      name: "3개월",
      price: "₩35,100",
      period: "/3개월",
      description: "월 ₩11,700 (10% 절약)",
      discount: "10% 할인",
      originalPrice: "₩38,700",
      link: `/payments/checkout?plan=3months&price=35100`,
      buttonText: "3개월 시작하기",
    },
    {
      name: "6개월",
      price: "₩64,600",
      period: "/6개월",
      description: "월 ₩10,767 (17% 절약)",
      discount: "17% 할인",
      originalPrice: "₩77,400",
      link: `/payments/checkout?plan=6months&price=64600`,
      buttonText: "6개월 시작하기",
      popular: !userIsPaid, // 무료 사용자에게만 추천 표시
    },
  ];

  // School Plan Data
  const schoolPlan = {
    name: "School",
    price: "문의",
    description: "교육 기관을 위한 특별 플랜",
    features: [
      "다중 사용자 관리",
      "교사 대시보드",
      "학생 진도 추적",
      "커스텀 학습 콘텐츠",
      "API 접근",
      "전담 지원",
      "온사이트 교육",
    ],
    buttonText: "문의하기",
    link: "/contact",
  };

  return (
    <div className="container mx-auto px-4 py-16">
      {/* Header */}
      <div className="mb-16 text-center">
        <h1 className="mb-4 text-4xl font-bold">
          학습에 맞는 플랜을 선택하세요
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-xl">
          AI 내동생과 함께 효과적으로 학습하고 성장하세요.
        </p>
      </div>

      {/* Free Plan */}
      <div className="mx-auto mb-16 max-w-md">
        <Card className="border-border relative">
          {!userIsPaid && (
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 transform">
              <div className="rounded-full bg-green-500 px-4 py-2 text-sm font-medium text-white">
                사용중
              </div>
            </div>
          )}

          <CardHeader className="pb-2 text-center">
            <CardTitle className="text-2xl">{freePlan.name}</CardTitle>
            <div className="my-2 text-4xl font-bold">{freePlan.price}</div>
            <CardDescription className="text-base">
              {freePlan.description}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-4">
            <ul className="space-y-3">
              {freePlan.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  {feature.included ? (
                    <CheckIcon className="mt-0.5 size-5 shrink-0 text-green-500" />
                  ) : (
                    <XIcon className="text-muted-foreground mt-0.5 size-5 shrink-0" />
                  )}
                  <span
                    className={
                      feature.included
                        ? "text-foreground"
                        : "text-muted-foreground line-through"
                    }
                  >
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>

          <CardFooter>
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              disabled={!userIsPaid}
            >
              {!userIsPaid ? "현재 플랜" : "무료 플랜"}
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* Pro Section */}
      <div className="mb-16">
        {/* Pro Features */}
        <div className="relative mx-auto mb-8 max-w-4xl rounded-2xl bg-accent p-8">
          {/* 사용중 표시 - 유료 사용자만 */}
          {userIsPaid && subscriptionInfo && (
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 transform">
              <div className="rounded-full bg-green-500 px-4 py-2 text-sm font-medium text-white shadow-lg">
                사용중
              </div>
            </div>
          )}
          <div className="mb-6 text-center">
            <div className="relative inline-block">
              <div className="mb-1 text-2xl font-semibold">Pro</div>
              <h2 className="mb-4 text-3xl font-bold">프로 플랜</h2>
            </div>

            {/* 사용 기간 표시 - 유료 사용자만 */}
            {userIsPaid && subscriptionInfo && (
              <div className="mb-4">
                <div className="inline-block rounded-lg bg-green-100 px-4 py-2">
                  <div className="text-sm font-medium text-green-800">
                    📅 사용 기간: {new Date().toLocaleDateString("ko-KR")} ~{" "}
                    {new Date(
                      subscriptionInfo.subscriptionEnd,
                    ).toLocaleDateString("ko-KR")}
                  </div>
                  {subscriptionInfo.isExtension && (
                    <div className="mt-1 text-xs text-green-600">
                      ✅ 구독 연장됨
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="text-muted-foreground mx-auto max-w-3xl text-xl">
              무제한 AI 학습으로 학습 효율을 높이세요
            </p>
          </div>

          <div className="mb-6 grid gap-6 md:grid-cols-2">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckIcon className="size-5 text-green-600" />
                <span className="font-medium">무제한 AI 피드백</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckIcon className="size-5 text-green-600" />
                <span className="font-medium">고급 개념 평가</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckIcon className="size-5 text-green-600" />
                <span className="font-medium">상세한 학습 분석</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckIcon className="size-5 text-green-600" />
                <span className="font-medium">학습 통계 및 리포트</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckIcon className="size-5 text-green-600" />
                <span className="font-medium">우선 지원</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckIcon className="size-5 text-green-600" />
                <span className="font-medium">광고 없는 환경</span>
              </div>
              
            </div>
          </div>

          

          {/* Pro Pricing Options - 소개 박스 안에 포함 */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {premiumPackages.map((pkg) => (
              <Card
                key={pkg.name}
                className={`relative flex flex-col ${
                  pkg.popular && !userIsPaid
                    ? "border-primary scale-105 shadow-xl"
                    : "border-border"
                }`}
              >
                {pkg.popular && !userIsPaid && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
                    <div className="bg-primary text-primary-foreground rounded-full px-3 py-1 text-sm font-medium">
                      추천
                    </div>
                  </div>
                )}

                <CardHeader className="pb-2 text-center">
                  <CardTitle className="text-xl">{pkg.name}</CardTitle>

                  {pkg.discount && (
                    <div className="mb-2">
                      <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-bold text-red-800">
                        {pkg.discount}
                      </span>
                    </div>
                  )}

                  {pkg.originalPrice && (
                    <span className="text-muted-foreground text-sm line-through">
                      {pkg.originalPrice}
                    </span>
                  )}

                  <div className="my-2 text-3xl font-bold text-primary">
                    {pkg.price}
                  </div>
                  <span className="text-muted-foreground text-sm">
                    {pkg.period}
                  </span>

                  <CardDescription className="mt-2 text-sm">
                    {pkg.description}
                  </CardDescription>
                </CardHeader>

                

                <CardFooter className="mt-auto flex flex-col gap-2">
                  <Button
                    asChild
                    variant={pkg.popular && !userIsPaid ? "default" : "outline"}
                    size="lg"
                    className="w-full"
                  >
                    <Link to={pkg.link}>{pkg.buttonText}</Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* School Plan */}
      <div className="mx-auto mb-16 max-w-md">
        <Card className="border-border relative">
          <CardHeader className="pb-2 text-center">
            <CardTitle className="text-2xl">{schoolPlan.name}</CardTitle>
            <div className="my-2 text-4xl font-bold">{schoolPlan.price}</div>
            <CardDescription className="text-base">
              {schoolPlan.description}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-4">
            <ul className="space-y-3">
              {schoolPlan.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckIcon className="mt-0.5 size-5 shrink-0 text-green-500" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>

          <CardFooter>
            <Button asChild variant="outline" size="lg" className="w-full">
              <Link to={schoolPlan.link}>{schoolPlan.buttonText}</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* CTA Section - 무료 사용자 및 비로그인 사용자에게만 표시 */}
      {!userIsPaid && (
        <div className="bg-muted mt-24 rounded-2xl p-12 text-center">
          <h2 className="mb-4 text-3xl font-bold">아직 고민중이신가요?</h2>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            무료 플랜으로 AI 내동생을 먼저 체험해보세요. 언제든지 프로 플랜으로
            업그레이드할 수 있습니다.
          </p>
          <div className="flex justify-center gap-4">
            <Button asChild size="lg">
              <Link to="/explain">무료로 시작하기</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contact">문의하기</Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
